import { LyfeTooltip } from "@lyfegen-platform/react-common";
import { IconButton, Typography } from "@material-ui/core";
import { MAX_SIMULATION_COMPARISON } from "@lyfegen-platform/utils";
import { useTranslation } from "react-i18next";
import ComparisonIcon from "apps/platform-client/client/src/components/icons/Comparison";
import { useRecoilState } from "recoil";
import { comparisonSimulations as comparisonSimulationsRecoil } from "apps/platform-client/client/src/store/simulator";
import ComparisonUpdateErrorAlert from "./ComparisonUpdateAlertError";
import { useDebouncedSimulationComparisonUpdate } from "./useDebouncedSimulationUpdate";
import { useEffect, useState } from "react";

interface ComparisonIconButtonProps {
	simulationId: string;
	isSimulationComplete: boolean;
}

export const ComparisonIconButton: React.FC<ComparisonIconButtonProps> = ({ simulationId, isSimulationComplete }) => {
	const { triggerUpdate, simulationErrorId, setSimulationErrorId, isRemoveComparison } = useDebouncedSimulationComparisonUpdate();
	const [simulations, setSimulations] = useRecoilState(comparisonSimulationsRecoil);
	const [previousSimulations] = useState<string[]>(simulations);
	const { t } = useTranslation();
	const maxComparisonReached = simulations.length === MAX_SIMULATION_COMPARISON;
	const disabled = (maxComparisonReached && !simulations.includes(simulationId)) || isSimulationComplete === false;
	const isInComparison = simulations?.includes?.(simulationId as string);

	const getTooltipTitle = () => {
		if (isInComparison) {
			return "remove-from-comparison";
		}

		if (maxComparisonReached) {
			return "simulator-comparison-disabled";
		}
		if (!isSimulationComplete) {
			return "simulator-comparison-disabled-draft";
		}

		return "add-to-comparison";
	};

	const tooltipTitle = getTooltipTitle();

	const handleComparison = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		if (disabled) {
			return;
		}

		const isRemoving = simulations.includes(simulationId);
		const updated = isRemoving ? simulations.filter((id) => id !== simulationId) : [...simulations, simulationId];

		setSimulations(updated);
		triggerUpdate(simulationId, updated, isRemoving);
	};

	useEffect(() => {
		if (simulationErrorId && previousSimulations) {
			setSimulations(previousSimulations);
			console.log("set previousSimulations", previousSimulations, simulationErrorId);
		}
	}, [simulationErrorId]);

	const cursorStyle = { cursor: disabled ? "auto" : "pointer" };

	return (
		<>
			<LyfeTooltip title={t(tooltipTitle)} style={cursorStyle}>
				<Typography>
					<IconButton
						onClick={handleComparison}
						style={cursorStyle}
						data-type="functional-button"
						data-functionality={tooltipTitle}
					>
						<ComparisonIcon isActive={isInComparison} />
					</IconButton>
				</Typography>
			</LyfeTooltip>
			{simulationErrorId === simulationId && (
				<ComparisonUpdateErrorAlert
					errorId={simulationErrorId}
					setSimulationError={setSimulationErrorId}
					isRemoveComparison={isRemoveComparison}
				/>
			)}
		</>
	);
};
